import {
  BigDarkLeaf,
  BigWhiteLeaf,
  MobileDark,
  MobileDarkLeaf,
  MobileWhite,
  MobileWhiteLeaf,
  NormalDark,
  NormalWhite,
} from '@icons/Leaves';
import { NextLink } from 'components/NextLink';
import { COLORS } from 'styles/constants';
import { Curve } from '../Curve';
import FadeInSection from '../FadeInSection';
import { LiquidButton } from '../LiquidButton';
import {
  ButtonMobileStyled,
  ButtonStyled,
  CopyContainer,
  DescriptionText,
  DesktopLeaf,
  LowerRightCornerAsset,
  MobileLeaf,
  Object1,
  Object2,
  Object3,
  Object4,
  Object5,
  ParentContainer,
  ProductTitle,
  SuperText,
  UpperLeftCornerAsset,
} from './styles';

export interface ISustainabilityBannerProps {
  superText?: string;
  title?: string;
  description?: string;
  link?: ILinkProps;
  upperLeftCorner?: IAssetsComponent;
  lowerRightCorner?: IAssetsComponent;
  children?: any;
  ctaColor?: string;
  id?: string;
}

interface ILinkProps {
  url: string;
  displayName: string;
}

interface IAssetsComponent {
  id: string;
  asset: IAsset[];
}

interface IAsset {
  id: string;
  url: string;
  altText: string;
}

const LiquidButtonElement = ({
  text = '',
  width,
  ctaColor = COLORS.primary,
}: {
  text: string;
  width: number;
  ctaColor?: string;
}) => (
  <LiquidButton
    text={text}
    height={50}
    width={width}
    color={ctaColor || COLORS.primary}
    textColor={COLORS.white}
  />
);

export const SustainabilityBanner = ({
  superText,
  title,
  description,
  // upperLeftCorner,
  // lowerRightCorner,
  link,
  ctaColor,
  id = 'sustainability-banner',
}: ISustainabilityBannerProps) => {
  const linkElement = link?.url && (
    <div>
      <NextLink href={link.url} className="inline-block">
        <span className="sr-only">{link.displayName}</span>
        <ButtonStyled>
          <LiquidButtonElement text={link.displayName} width={250} ctaColor={ctaColor || ''} />
        </ButtonStyled>
        <ButtonMobileStyled>
          <LiquidButtonElement text={link.displayName} width={200} ctaColor={ctaColor || ''} />
        </ButtonMobileStyled>
      </NextLink>
    </div>
  );

  const upperCutLeaves = (
    <>
      <Object1 data-test={[`${id}-object1`]}>
        <FadeInSection spinUp delay="1000ms">
          <DesktopLeaf>
            <BigWhiteLeaf />
          </DesktopLeaf>
          <MobileLeaf>
            <MobileWhiteLeaf />
          </MobileLeaf>
        </FadeInSection>
      </Object1>
      <Object2 data-test={[`${id}-object2`]}>
        <FadeInSection spinUp delay="1000ms">
          <DesktopLeaf>
            <NormalDark />
          </DesktopLeaf>
          <MobileLeaf>
            <MobileDark />
          </MobileLeaf>
        </FadeInSection>
      </Object2>
    </>
  );

  const lowerCutLeaves = (
    <>
      <Object4 data-test={[`${id}-object4`]}>
        <FadeInSection spinUp delay="1000ms">
          <DesktopLeaf>
            <NormalWhite />
          </DesktopLeaf>
          <MobileLeaf>
            <MobileWhite />
          </MobileLeaf>
        </FadeInSection>
      </Object4>
      <Object5 data-test={[`${id}-object5`]}>
        <FadeInSection spinUp delay="1000ms">
          <DesktopLeaf>
            <BigDarkLeaf />
          </DesktopLeaf>
          <MobileLeaf>
            <MobileDarkLeaf />
          </MobileLeaf>
        </FadeInSection>
      </Object5>
    </>
  );

  const middleLeaves = (
    <Object3 data-test={[`${id}-object3`]}>
      <FadeInSection spinUp delay="1000ms">
        <DesktopLeaf>
          <NormalDark />
        </DesktopLeaf>
        <MobileLeaf>
          <MobileDark />
        </MobileLeaf>
      </FadeInSection>
    </Object3>
  );
  return (
    <ParentContainer data-test={[`${id}`]} data-print="hide">
      <FadeInSection>
        <FadeInSection delay="500ms" spinUp>
          <FadeInSection delay="1000ms">
            <CopyContainer data-test={[`${id}-mobile-copy-container`]} data-print="hide">
              {upperCutLeaves}
              {middleLeaves}

              {lowerCutLeaves}
              <UpperLeftCornerAsset data-test={[`${id}-mobile-upper-left-asset`]}>
                <FadeInSection delay="800ms">
                  {/* <NextImage
                    sources={getPictureSources(upperLeftCorner?.asset[0])}
                    alt={upperLeftCorner?.asset[0].altText}
                  /> */}
                </FadeInSection>
              </UpperLeftCornerAsset>

              <div
                data-test="main-content"
                className="flex flex-col grow justify-center items-center relative w-full"
              >
                {superText && (
                  <SuperText data-test={[`${id}-mobile-super-text`]}>{superText}</SuperText>
                )}
                {title && (
                  <ProductTitle tag="h3" type="5xl" data-test={[`${id}-mobile-title`]}>
                    {title}
                  </ProductTitle>
                )}
                {description && (
                  <DescriptionText data-test={[`${id}-mobile-description`]}>
                    {description}
                  </DescriptionText>
                )}
                {linkElement}
              </div>
              <LowerRightCornerAsset data-test={[`${id}-mobile-lower-right-asset`]}>
                <FadeInSection delay="800ms">
                  {/* <NextImage
                    sources={getPictureSources(lowerRightCorner?.asset[0])}
                    alt={lowerRightCorner?.asset[0].altText}
                  /> */}
                </FadeInSection>
              </LowerRightCornerAsset>
              <Curve
                type="top"
                className="hidden md:block top-0 h-61.25 bg-white w-[114%] left-[-7%]"
              />
              <Curve
                type="bottom"
                className="hidden md:block h-61.25 bg-white w-[114%] left-[-7%]"
              />
              <Curve
                type="top"
                className="block md:hidden top-0 h-31.5 bg-white w-[114%] left-[-7%]"
              />
              <Curve
                type="bottom"
                className="block md:hidden h-31.5 bg-white w-[114%] left-[-7%]"
              />
            </CopyContainer>
          </FadeInSection>
        </FadeInSection>
      </FadeInSection>
    </ParentContainer>
  );
};
